import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Stack,
  Paper,
  useTheme,
  alpha,
  Tooltip,
  Fade,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { AssessmentStatusHeaderProps } from './types';

export const AssessmentStatusHeader: React.FC<AssessmentStatusHeaderProps> = ({
  totalAssessments,
  completedAssessments,
  onGenerateReport,
  canGenerateInitial,
  canGenerateReview,
  isGenerating,
  userRole,
}) => {
  const theme = useTheme();
  const [displayedCount, setDisplayedCount] = useState(0);
  const percentage = totalAssessments > 0 ? (completedAssessments / totalAssessments) * 100 : 0;

  // Determine if showing override buttons for admin/manager
  const canOverride = userRole === 'ADMIN' || userRole === 'MANAGER';
  const showInitialAsOverride = canOverride && !canGenerateInitial;
  const showReviewAsOverride = canOverride && !canGenerateReview;

  // Animated counter effect
  useEffect(() => {
    const duration = 1000; // 1 second
    const steps = 30;
    const stepDuration = duration / steps;
    const increment = completedAssessments / steps;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      setDisplayedCount(Math.min(Math.round(increment * currentStep), completedAssessments));
      
      if (currentStep >= steps) {
        clearInterval(timer);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [completedAssessments]);

  const getStatusColor = () => {
    if (percentage === 100) return theme.palette.success.main;
    if (percentage >= 75) return theme.palette.info.main;
    if (percentage >= 50) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const getStatusMessage = () => {
    if (percentage === 100) return 'All assessments complete!';
    if (percentage >= 75) return 'Almost there!';
    if (percentage >= 50) return 'Good progress';
    return 'Getting started';
  };

  const buttonVariants = {
    initial: { scale: 0.95, opacity: 0 },
    animate: { 
      scale: 1, 
      opacity: 1,
      transition: {
        type: 'spring' as const,
        stiffness: 200,
        damping: 15,
      },
    },
    hover: {
      scale: 1.05,
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.98 },
  };

  const confettiVariants = {
    initial: { opacity: 0, scale: 0 },
    animate: {
      opacity: [0, 1, 0],
      scale: [0, 1.5, 0],
      rotate: [0, 180, 360],
      transition: {
        duration: 2,
        ease: 'easeOut' as const,
      },
    },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, transparent 100%)`,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderRadius: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(getStatusColor(), 0.1)} 0%, transparent 70%)`,
        }}
      />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="center" justifyContent="space-between">
        <Box>
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <motion.div
              animate={{
                rotate: [0, 360],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: 'linear',
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: alpha(getStatusColor(), 0.1),
                  color: getStatusColor(),
                }}
              >
                <AssessmentIcon />
              </Box>
            </motion.div>

            <Box>
              <Typography variant="h5" fontWeight={700}>
                Assessment Status
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getStatusMessage()}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={4} alignItems="center">
            <Box position="relative" display="inline-flex">
              <CircularProgress
                variant="determinate"
                value={percentage}
                size={80}
                thickness={4}
                sx={{
                  color: getStatusColor(),
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  },
                }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h6" component="div" fontWeight={700} color={getStatusColor()}>
                  {displayedCount}/{totalAssessments}
                </Typography>
              </Box>
              
              {percentage === 100 && (
                <motion.div
                  variants={confettiVariants}
                  initial="initial"
                  animate="animate"
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <AutoAwesomeIcon sx={{ color: theme.palette.warning.main, fontSize: 40 }} />
                </motion.div>
              )}
            </Box>

            <Box>
              <Stack spacing={0.5}>
                <Typography variant="h3" fontWeight={800} color={getStatusColor()}>
                  {percentage.toFixed(0)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Complete
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </Box>

        <Stack direction={{ xs: 'row', sm: 'column' }} spacing={2}>
          <AnimatePresence>
            {(canGenerateInitial || canOverride) && (
              <motion.div
                variants={buttonVariants}
                initial="initial"
                animate="animate"
                exit="initial"
                whileHover="hover"
                whileTap="tap"
              >
                <Tooltip
                  title={showInitialAsOverride
                    ? "Admin override: Generate report without complete assessments"
                    : "All initial assessments are complete"
                  }
                  placement="top"
                >
                  <Button
                    variant="contained"
                    color={showInitialAsOverride ? "warning" : "primary"}
                    startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                    onClick={() => onGenerateReport('Initial', showInitialAsOverride)}
                    disabled={isGenerating}
                    sx={{
                      minWidth: 180,
                      boxShadow: 3,
                      '&:hover': {
                        boxShadow: 6,
                      },
                    }}
                  >
                    {showInitialAsOverride ? "Manual Override: Initial" : "Generate Initial Report"}
                  </Button>
                </Tooltip>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {(canGenerateReview || canOverride) && (
              <motion.div
                variants={buttonVariants}
                initial="initial"
                animate="animate"
                exit="initial"
                whileHover="hover"
                whileTap="tap"
              >
                <Tooltip
                  title={showReviewAsOverride
                    ? "Admin override: Generate report without complete assessments"
                    : "All review assessments are complete"
                  }
                  placement="top"
                >
                  <Button
                    variant="contained"
                    color={showReviewAsOverride ? "warning" : "secondary"}
                    startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                    onClick={() => onGenerateReport('Review', showReviewAsOverride)}
                    disabled={isGenerating}
                    sx={{
                      minWidth: 180,
                      boxShadow: 3,
                      '&:hover': {
                        boxShadow: 6,
                      },
                    }}
                  >
                    {showReviewAsOverride ? "Manual Override: Review" : "Generate Review Report"}
                  </Button>
                </Tooltip>
              </motion.div>
            )}
          </AnimatePresence>
        </Stack>
      </Stack>

      {/* Success animation overlay */}
      <AnimatePresence>
        {percentage === 100 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.success.light} 0%, ${theme.palette.success.main} 50%, ${theme.palette.success.light} 100%)`,
            }}
          >
            <motion.div
              animate={{
                x: ['0%', '100%'],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'linear',
              }}
              style={{
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: `linear-gradient(90deg, transparent 0%, ${alpha(theme.palette.common.white, 0.5)} 50%, transparent 100%)`,
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </Paper>
  );
};